import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Loader2, Tag, X } from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { Modal } from './ui/Modal'
import { useAuthStore } from '../stores/authStore'
import { startRazorpayCheckout } from '../lib/payments'
import { formatMoney } from '../lib/format'

/** Money for the pricing summary: the currency's symbol + the person's
 *  number format (shared `formatMoney`); a currency without a known
 *  symbol renders as "CODE 0.00". */
function money(amount, currency = 'INR') {
  return formatMoney(Number(amount ?? 0), String(currency || 'INR').toUpperCase())
}

/** One line in the pricing summary — muted label on the left, value on
 *  the right. `emphasis` renders the final payable in the strong tone. */
function SummaryRow({ label, value, tone = 'default', emphasis = false }) {
  const valueClass =
    tone === 'credit'
      ? 'text-[color:var(--color-success)]'
      : emphasis
        ? 'text-[color:var(--color-ink-strong)] text-base font-semibold'
        : 'text-[color:var(--color-ink)]'
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span
        className={
          emphasis
            ? 'font-semibold text-[color:var(--color-ink-strong)]'
            : 'text-[color:var(--color-ink-muted)]'
        }
      >
        {label}
      </span>
      <span className={`tabular-nums ${valueClass}`}>{value}</span>
    </div>
  )
}

/**
 * Plan checkout dialog — the "complete checkout experience" for a paid
 * upgrade. Reuses the existing infrastructure end-to-end:
 *   • `POST /payments/quote` for the live pricing summary (plan price,
 *     mid-cycle proration credit, coupon discount, tax, final payable).
 *     The quote is the SAME server computation `/payments/order` charges,
 *     so what's shown here can't drift from what the gateway authorises.
 *   • `startRazorpayCheckout` for the actual payment — unchanged.
 *
 * No pricing, proration, or coupon logic is duplicated on the client.
 */
export function PlanCheckoutModal({ plan, open, onClose, onPaid, defaultCadence = 'monthly' }) {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)

  // A cadence is offered only when the plan carries a positive price for
  // it. A plan priced only yearly (blank monthly) checks out on the yearly
  // cycle — Monthly is disabled and Yearly is the default, and vice versa.
  const hasMonthly = useMemo(() => {
    const m = plan?.monthlyPrice
    return m != null && m !== '' && Number(m) > 0
  }, [plan])
  const hasYearly = useMemo(() => {
    const y = plan?.yearlyPrice
    return y != null && y !== '' && Number(y) > 0
  }, [plan])

  // Open on the cycle the customer picked on the Billing page when the plan
  // offers it; otherwise fall back to whichever cycle the plan is priced in.
  const initialCadence =
    defaultCadence === 'yearly' && hasYearly
      ? 'yearly'
      : defaultCadence === 'monthly' && hasMonthly
        ? 'monthly'
        : hasMonthly
          ? 'monthly'
          : 'yearly'
  const [cadence, setCadence] = useState(initialCadence)
  // The coupon that's actually applied to the quote. Seeded from the code
  // the tenant entered at sign-up (remembered server-side).
  const [appliedCoupon, setAppliedCoupon] = useState(
    tenant?.billing?.pendingCouponCode ?? '',
  )
  const [couponInput, setCouponInput] = useState(
    tenant?.billing?.pendingCouponCode ?? '',
  )
  const [paying, setPaying] = useState(false)

  const quoteQuery = useQuery({
    queryKey: ['payment-quote', plan?.code, cadence, appliedCoupon],
    enabled: Boolean(open && plan?.code),
    queryFn: async () => {
      const body = { planCode: plan.code, cadence }
      if (appliedCoupon && appliedCoupon.trim())
        body.couponCode = appliedCoupon.trim()
      const res = await api.post('/payments/quote', body)
      return res.data
    },
    placeholderData: (prev) => prev,
  })

  const quote = quoteQuery.data
  const currency = quote?.currency ?? plan?.currency ?? 'INR'
  const couponApplied = Boolean(appliedCoupon && quote?.couponValid)
  const couponError =
    appliedCoupon && quote && !quote.couponValid ? quote.couponReason : null

  function applyCoupon() {
    const code = couponInput.trim()
    if (!code) return
    setAppliedCoupon(code)
  }
  function removeCoupon() {
    setAppliedCoupon('')
    setCouponInput('')
  }

  async function handlePay() {
    if (paying) return
    setPaying(true)
    await startRazorpayCheckout({
      planCode: plan.code,
      cadence,
      // Only send a code the customer actually applied.
      couponCode: appliedCoupon || undefined,
      user,
      onSuccess: async () => {
        await onPaid?.()
        onClose()
      },
      // Fires on dismiss / failure / after success — re-enable Pay so the
      // customer can retry without reopening the dialog.
      onClose: () => setPaying(false),
    })
  }

  if (!plan) return null

  const payDisabled =
    paying ||
    quoteQuery.isLoading ||
    quoteQuery.isError ||
    !quote ||
    Number(quote.finalPayable) <= 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Upgrade to ${plan.name}`}
      description="Review your plan, apply a coupon, and pay to upgrade."
      size="ml"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={paying}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-60 cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handlePay}
            disabled={payDisabled}
            className="taskmagix-btn-primary inline-flex items-center justify-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {paying ? (
              <>
                <Loader2 size={15} className="animate-spin" />
                Processing…
              </>
            ) : quote ? (
              `Pay ${money(quote.finalPayable, currency)}`
            ) : (
              'Pay & Upgrade'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Selected plan + billing cycle */}
        <div>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm text-[color:var(--color-ink-muted)]">
              Selected plan
            </span>
            <span className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
              {plan.name}
            </span>
          </div>

          <div className="mt-3">
            <span className="text-sm text-[color:var(--color-ink-muted)]">
              Billing cycle
            </span>
            <div className="mt-1.5 inline-flex rounded-lg border border-[color:var(--color-rule)] p-0.5 bg-[color:var(--color-canvas)]">
              <button
                type="button"
                onClick={() => hasMonthly && setCadence('monthly')}
                disabled={!hasMonthly}
                title={hasMonthly ? undefined : 'Monthly billing is not available for this plan.'}
                className={
                  'px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ' +
                  (!hasMonthly
                    ? 'text-[color:var(--color-ink-subtle)] cursor-not-allowed'
                    : cadence === 'monthly'
                      ? 'bg-[color:var(--color-card)] text-[color:var(--color-ink-strong)] shadow-sm cursor-pointer'
                      : 'text-[color:var(--color-ink-muted)] cursor-pointer')
                }
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => hasYearly && setCadence('yearly')}
                disabled={!hasYearly}
                title={hasYearly ? undefined : 'Yearly billing is not available for this plan.'}
                className={
                  'px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ' +
                  (!hasYearly
                    ? 'text-[color:var(--color-ink-subtle)] cursor-not-allowed'
                    : cadence === 'yearly'
                      ? 'bg-[color:var(--color-card)] text-[color:var(--color-ink-strong)] shadow-sm cursor-pointer'
                      : 'text-[color:var(--color-ink-muted)] cursor-pointer')
                }
              >
                Yearly
              </button>
            </div>
          </div>
        </div>

        {/* Coupon */}
        <div>
          <span className="text-sm text-[color:var(--color-ink-muted)]">
            Coupon code
          </span>
          {couponApplied ? (
            <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-[color:var(--color-success)] bg-[color:var(--color-success-bg)] px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--color-success)]">
                <Check size={15} strokeWidth={2.5} />
                {quote.couponCode} applied
              </span>
              <button
                type="button"
                onClick={removeCoupon}
                className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
              >
                <X size={13} /> Remove
              </button>
            </div>
          ) : (
            <>
              <div className="mt-1.5 flex items-stretch gap-2">
                <div className="relative flex-1">
                  <Tag
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-subtle)]"
                  />
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        applyCoupon()
                      }
                    }}
                    placeholder="Enter a coupon code"
                    maxLength={50}
                    className="w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] pl-8 pr-3 py-2 text-sm text-[color:var(--color-ink)] placeholder:text-[color:var(--color-ink-subtle)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-accent-strong)]"
                  />
                </div>
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={!couponInput.trim() || quoteQuery.isFetching}
                  className="px-4 rounded-lg text-sm font-semibold border border-[color:var(--color-accent-strong)] text-[color:var(--color-accent-strong)] hover:bg-[color:var(--color-accent-strong)] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  Apply
                </button>
              </div>
              {couponError ? (
                <p className="mt-1.5 text-sm text-[color:var(--color-priority-urgent)]">
                  {couponError}
                </p>
              ) : null}
            </>
          )}
        </div>

        {/* Pricing summary */}
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-4">
          {quoteQuery.isError ? (
            <p className="text-sm text-[color:var(--color-priority-urgent)]">
              {extractApiError(quoteQuery.error).message}
            </p>
          ) : !quote ? (
            <p className="inline-flex items-center gap-2 text-sm text-[color:var(--color-ink-muted)]">
              <Loader2 size={14} className="animate-spin" /> Calculating…
            </p>
          ) : (
            <div className="space-y-2.5">
              <SummaryRow
                label={`Plan price (${cadence === 'yearly' ? 'yearly' : 'monthly'})`}
                value={money(quote.originalPrice, currency)}
              />
              {Number(quote.prorationCredit) > 0 ? (
                <SummaryRow
                  label="Proration credit (unused time)"
                  value={`− ${money(quote.prorationCredit, currency)}`}
                  tone="credit"
                />
              ) : null}
              {Number(quote.discountAmount) > 0 ? (
                <SummaryRow
                  label={`Discount${quote.couponCode ? ` (${quote.couponCode})` : ''}`}
                  value={`− ${money(quote.discountAmount, currency)}`}
                  tone="credit"
                />
              ) : null}
              <SummaryRow label="Tax" value={money(quote.tax, currency)} />
              <div className="border-t border-[color:var(--color-rule)] pt-2.5">
                <SummaryRow
                  label="Total payable"
                  value={money(quote.finalPayable, currency)}
                  emphasis
                />
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-[color:var(--color-ink-subtle)]">
          Payments are processed securely by Razorpay. Your subscription
          activates as soon as the payment is confirmed.
        </p>
      </div>
    </Modal>
  )
}
